import React, { useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { ProgressRing } from "@/components/common/ProgressRing";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CreateDrawer } from "@/components/common/CreateDrawer";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Skeletons } from "@/components/common/EmptyState";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { RocketLaunch, CheckCircle, Circle, Warning, Plus, PencilSimple, Copy, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEFAULT_CHECKLIST = [
  "Product design finalised", "Photography completed", "Website listing ready",
  "Campaign creatives ready", "Influencers confirmed", "Ads prepared",
];
const FIELDS = [
  { name: "name", label: "Launch Name", required: true },
  { name: "brand", label: "Brand", type: "select" },
  { name: "launch_date", label: "Launch Date", type: "date", required: true },
  { name: "owner", label: "Owner" },
];

export default function Launches() {
  const { items, isLoading, create, update, remove } = useResource("launches");
  const { isApprover } = useAuth();
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const toggle = (launch, idx) => {
    const checklist = launch.checklist.map((c, i) => (i === idx ? { ...c, done: !c.done } : c));
    const readiness = Math.round((checklist.filter((c) => c.done).length / checklist.length) * 100);
    update.mutate({ id: launch.id, body: { checklist, readiness, status: readiness > 70 ? "On track" : readiness > 40 ? "At risk" : "Blocked" } });
  };
  const duplicate = async (l) => { const { id, created_at, _id, ...rest } = l; await create.mutateAsync({ ...rest, name: rest.name + " (copy)" }); toast.success("Duplicated"); };
  const del = async (l) => { try { await remove.mutateAsync(l.id); toast.success("Deleted"); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } setConfirm(null); };

  return (
    <div>
      <PageHeader title="Product Launches" subtitle="Launch control centre with readiness tracking" icon={RocketLaunch}
        actions={<Button onClick={() => { setEditItem(null); setOpen(true); }} data-testid="add-btn" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><Plus size={16} weight="bold" className="mr-1" /> New Launch</Button>} />
      {isLoading ? <Skeletons /> : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {items.map((l, i) => (
            <div key={l.id} data-testid="launch-card" className="group overflow-hidden rounded-2xl border border-border bg-surface-raised" style={{ animation: `fade-up 0.4s ease-out ${i * 0.05}s both` }}>
              <div className="relative h-32">
                <img src={l.cover} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface-raised via-surface-raised/40 to-transparent" />
                <div className="absolute right-3 top-3 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => { setEditItem(l); setOpen(true); }} data-testid="edit-btn" className="rounded-lg bg-black/40 p-1.5 text-white backdrop-blur hover:bg-black/60"><PencilSimple size={14} /></button>
                  <button onClick={() => duplicate(l)} data-testid="duplicate-btn" className="rounded-lg bg-black/40 p-1.5 text-white backdrop-blur hover:bg-black/60"><Copy size={14} /></button>
                  {isApprover && <button onClick={() => setConfirm(l)} data-testid="delete-btn" className="rounded-lg bg-black/40 p-1.5 text-white backdrop-blur hover:bg-black/60"><Trash size={14} /></button>}
                </div>
                <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                  <div><p className="font-display text-lg font-black">{l.name}</p><p className="text-xs text-muted-foreground">{l.brand} · {new Date(l.launch_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</p></div>
                  <StatusBadge status={l.status} />
                </div>
              </div>
              <div className="flex items-center gap-4 p-5">
                <ProgressRing value={l.readiness} size={80} stroke={8} color={l.readiness > 70 ? "#A3E635" : l.readiness > 40 ? "#FACC15" : "#FB7185"} />
                <div className="flex-1 space-y-1.5">
                  {l.checklist.map((c, idx) => (
                    <button key={idx} onClick={() => toggle(l, idx)} data-testid="checklist-item" className="flex w-full items-center gap-2 text-left text-sm">
                      {c.done ? <CheckCircle size={18} weight="fill" className="text-lime-400" /> : <Circle size={18} className="text-muted-foreground" />}
                      <span className={c.done ? "text-muted-foreground line-through" : ""}>{c.label}</span>
                    </button>
                  ))}
                  {l.blockers > 0 && <p className="mt-2 flex items-center gap-1 text-xs text-brand-pink"><Warning size={14} weight="fill" /> {l.blockers} blocker{l.blockers > 1 ? "s" : ""}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <CreateDrawer open={open} onOpenChange={setOpen} title="Launch" fields={FIELDS} mode={editItem ? "edit" : "create"} initial={editItem} submitting={create.isPending || update.isPending}
        onSubmit={(body) => editItem ? update.mutateAsync({ id: editItem.id, body }) : create.mutateAsync({ ...body, readiness: 0, status: "Blocked", blockers: 0, cover: "https://images.pexels.com/photos/1462637/pexels-photo-1462637.jpeg?auto=compress&w=800", checklist: DEFAULT_CHECKLIST.map((label) => ({ label, done: false })) })} />
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title="Delete this launch?" onConfirm={() => del(confirm)} />
    </div>
  );
}
